import { useEffect, useState } from "react";
import { formatApiError } from "@/lib/auth";
import { fetchCategories } from "@/lib/api";
import { supabase, getStorageUrl } from "@/lib/supabase";
import { Plus, Pencil, Trash2, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "",
  slug: "",
  image: "",
  accent: "#FF3366",
};

export default function CategoriesTab() {
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null); // null | EMPTY | category
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const c = await fetchCategories();
      setCategories(c);
    } catch (e) {
      toast.error("Failed to load categories: " + (e.message || ""));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (slug) => {
    if (!window.confirm("Delete this category? This might fail if products are linked to it.")) return;
    try {
      const { error } = await supabase.from("categories").delete().eq("slug", slug);
      if (error) {
        // Catch foreign key constraint violations
        if (error.code === '23503') {
          throw new Error(`Cannot delete this category because there are still products inside it. Please delete the products in this category first, or update your database schema to cascade deletes.`);
        }
        throw error;
      }
      toast.success("Category deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e) || e.message);
    }
  };

  return (
    <div data-testid="admin-categories-tab">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h2 className="font-display font-black uppercase text-3xl md:text-4xl">Categories ({categories.length})</h2>
          <p className="text-black/70">Add, edit, or remove store categories.</p>
        </div>
        <button
          onClick={() => setEditing({ ...EMPTY })}
          data-testid="add-category-btn"
          className="nb-btn bg-[var(--primary)] text-white"
        >
          <Plus className="inline mr-1" size={18} strokeWidth={3} /> Add Category
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {categories.map((c) => (
          <div key={c.slug} className="nb-card p-4 flex gap-3" data-testid={`admin-category-${c.slug}`}>
            <div className="w-20 h-20 shrink-0 rounded-2xl border-[3px] border-black overflow-hidden bg-[var(--muted)]">
              {c.image ? (
                <img src={getStorageUrl(c.image)} alt={c.name} className="w-full h-full object-cover" />
              ) : (
                <div className="flex items-center justify-center w-full h-full">
                  <ImageIcon />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold truncate">{c.name}</div>
              <div className="text-xs uppercase tracking-wider text-black/60">{c.slug}</div>
              <div className="flex items-center gap-1.5 mt-1">
                <span className="text-xs font-bold">Accent:</span>
                <span
                  className="w-3.5 h-3.5 rounded-full border border-black inline-block"
                  style={{ backgroundColor: c.accent }}
                />
                <span className="text-xs text-black/60 font-mono">{c.accent}</span>
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => setEditing(c)}
                  className="nb-pill !text-xs !py-1.5"
                  data-testid={`edit-category-${c.slug}`}
                >
                  <Pencil size={12} strokeWidth={3} className="inline mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => remove(c.slug)}
                  className="nb-pill !text-xs !py-1.5 bg-black text-white"
                  data-testid={`delete-category-${c.slug}`}
                >
                  <Trash2 size={12} strokeWidth={3} className="inline mr-1" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <CategoryForm
          initial={editing}
          busy={busy}
          setBusy={setBusy}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

export function CategoryForm({ initial, busy, setBusy, onClose, onSaved }) {
  const [f, setF] = useState(initial);
  const isEdit = !!initial.slug; // If initial had a slug, we're editing

  // Helper to auto-generate slug
  const handleNameChange = (nameVal) => {
    const slugVal = nameVal
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "") // remove non-alphanumeric except space and hyphen
      .replace(/[\s_]+/g, "-") // replace space/underscore with hyphen
      .replace(/^-+|-+$/g, ""); // remove leading/trailing hyphens

    setF((cur) => ({
      ...cur,
      name: nameVal,
      slug: isEdit ? cur.slug : slugVal, // Only auto-generate if creating new
    }));
  };

  const upload = async (file) => {
    const ext = file.name.split(".").pop();
    const fileName = `${crypto.randomUUID()}.${ext}`;
    const { data, error } = await supabase.storage.from("images").upload(`categories/${fileName}`, file);
    if (error) throw error;
    return data.path;
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const path = await upload(files[0]);
      setF((cur) => ({ ...cur, image: path }));
      toast.success("Image uploaded successfully");
    } catch (err) {
      toast.error(formatApiError(err) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!f.name) {
      toast.error("Category name is required");
      return;
    }
    setBusy(true);
    try {
      const generatedSlug = f.slug || f.name
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, "")
        .replace(/[\s_]+/g, "-")
        .replace(/^-+|-+$/g, "");
      const defaultImage = f.image || "https://images.unsplash.com/photo-1523381210434-271e8be1f52b?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
      const defaultAccent = f.accent || "#FF3366";

      if (isEdit) {
        // Edit category
        const { error } = await supabase
          .from("categories")
          .update({
            name: f.name.trim(),
            image: defaultImage,
            accent: defaultAccent,
          })
          .eq("slug", initial.slug);
        if (error) throw error;
        toast.success("Category updated");
      } else {
        // Insert category
        const { error } = await supabase.from("categories").insert([
          {
            name: f.name.trim(),
            slug: generatedSlug,
            image: defaultImage,
            accent: defaultAccent,
          },
        ]);
        if (error) throw error;
        toast.success("Category created");
      }
      onSaved({
        name: f.name.trim(),
        slug: generatedSlug,
        image: defaultImage,
        accent: defaultAccent,
      });
    } catch (err) {
      toast.error(formatApiError(err) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="category-form-modal">
      <div className="nb-card bg-white max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-black uppercase text-2xl">{isEdit ? "Edit" : "Add"} Category</h3>
          <button onClick={onClose} className="nb-pill" data-testid="form-close">
            <X size={14} strokeWidth={3} />
          </button>
        </div>

        <div className="flex flex-col gap-4">
          <Field label="Category Name *">
            <input
              value={f.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="e.g. Jeans, Cargo Pants, Track Pants"
              data-testid="form-name"
              className="form-input"
              autoFocus
            />
          </Field>
        </div>

        <div className="mt-6 flex gap-3 justify-end">
          <button onClick={onClose} className="nb-pill" data-testid="form-cancel">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy}
            data-testid="form-save"
            className="nb-btn bg-[var(--primary)] text-white disabled:opacity-60"
          >
            {busy ? "Saving…" : isEdit ? "Update" : "Create"}
          </button>
        </div>
      </div>

      <style>{`
        .form-input { width:100%; padding: 0.55rem 1rem; border:3px solid #0A0A0A; border-radius: 9999px; box-shadow: 3px 3px 0 0 #0A0A0A; outline:none; background:#fff; }
        .form-input:focus { box-shadow: 4px 4px 0 0 #FF3366; }
        .form-input:disabled { background: #f3f4f6; cursor: not-allowed; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-sm font-black uppercase tracking-wider mb-1 inline-block">{label}</span>
      {children}
    </label>
  );
}
